import { useState, useRef, useEffect, useCallback } from 'react';

const THUMB_PERCENT = 40;
const HIDE_DELAY_MS = 1000;

interface HorizontalScrollIndicatorProps {
  readonly scrollRef: React.RefObject<HTMLElement | null>;
}

export const HorizontalScrollIndicator = ({ scrollRef }: HorizontalScrollIndicatorProps) => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [scrollInfo, setScrollInfo] = useState({ progress: 0, hasOverflow: false });
  const [interacting, setInteracting] = useState(false);
  const [visible, setVisible] = useState(false);
  const draggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, startProgress: 0 });
  const hideTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const scheduleHide = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => {
      if (!draggingRef.current) {
        setVisible(false);
      }
    }, HIDE_DELAY_MS);
  }, []);

  const showBar = useCallback(() => {
    clearTimeout(hideTimerRef.current);
    setVisible(true);
  }, []);

  const triggerHaptic = useCallback(() => {
    if (navigator.vibrate) {
      navigator.vibrate(8);
    }
  }, []);

  const updateScroll = useCallback(() => {
    if (draggingRef.current) return;
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    const hasOverflow = maxScroll > 0;
    const progress = hasOverflow ? el.scrollLeft / maxScroll : 0;
    setScrollInfo({ progress, hasOverflow });
    showBar();
    scheduleHide();
  }, [scrollRef, showBar, scheduleHide]);

  const scrollToProgress = useCallback((p: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const clamped = Math.max(0, Math.min(1, p));
    const maxScroll = el.scrollWidth - el.clientWidth;
    el.scrollLeft = clamped * maxScroll;
    setScrollInfo({ progress: clamped, hasOverflow: true });
  }, [scrollRef]);

  const handleTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const track = trackRef.current;
    if (!track) return;
    const rect = track.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const thumbWidth = rect.width * (THUMB_PERCENT / 100);
    const usable = rect.width - thumbWidth;
    const progress = (clickX - thumbWidth / 2) / usable;
    scrollToProgress(progress);
  }, [scrollToProgress]);

  const handleThumbDown = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    e.stopPropagation();
    e.preventDefault();
    draggingRef.current = true;
    setInteracting(true);
    clearTimeout(hideTimerRef.current);
    triggerHaptic();
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    dragStartRef.current = { x: clientX, startProgress: scrollInfo.progress };

    const onMove = (ev: MouseEvent | TouchEvent) => {
      const track = trackRef.current;
      if (!track) return;
      const moveX = 'touches' in ev ? ev.touches[0].clientX : ev.clientX;
      const rect = track.getBoundingClientRect();
      const thumbWidth = rect.width * (THUMB_PERCENT / 100);
      const usable = rect.width - thumbWidth;
      const dx = moveX - dragStartRef.current.x;
      const dp = dx / usable;
      scrollToProgress(dragStartRef.current.startProgress + dp);
    };

    const onUp = () => {
      draggingRef.current = false;
      setInteracting(false);
      document.body.style.userSelect = '';
      document.body.style.webkitUserSelect = '';
      scheduleHide();
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onUp);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onUp);
  }, [scrollInfo.progress, scrollToProgress, triggerHaptic, scheduleHide]);

  const handleTrackEnter = useCallback(() => {
    if (!draggingRef.current) {
      setInteracting(true);
      clearTimeout(hideTimerRef.current);
    }
  }, []);

  const handleTrackLeave = useCallback(() => {
    if (!draggingRef.current) {
      setInteracting(false);
      scheduleHide();
    }
  }, [scheduleHide]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    updateScroll();
    el.addEventListener('scroll', updateScroll, { passive: true });
    const observer = new ResizeObserver(updateScroll);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScroll);
      observer.disconnect();
      clearTimeout(hideTimerRef.current);
    };
  }, [scrollRef, updateScroll]);

  const isVisible = visible || interacting;

  return (
    <div
      ref={trackRef}
      onClick={handleTrackClick}
      onPointerEnter={handleTrackEnter}
      onPointerLeave={handleTrackLeave}
      style={{
        height: scrollInfo.hasOverflow
          ? interacting ? '10px' : '6px'
          : '1px',
        backgroundColor: interacting
          ? 'var(--border-subtle)'
          : 'var(--border-default)',
        position: 'relative',
        opacity: scrollInfo.hasOverflow ? (isVisible ? 1 : 0) : 1,
        pointerEvents: scrollInfo.hasOverflow ? (isVisible ? 'auto' : 'none') : 'none',
        transition: 'height 200ms cubic-bezier(0.23, 1, 0.32, 1), background-color 200ms ease, opacity 300ms ease',
        cursor: scrollInfo.hasOverflow ? 'pointer' : 'default',
      }}
    >
      {scrollInfo.hasOverflow && (
        <div
          onMouseDown={handleThumbDown}
          onTouchStart={handleThumbDown}
          style={{
            position: 'absolute',
            top: 0,
            left: `${scrollInfo.progress * (100 - THUMB_PERCENT)}%`,
            width: `${THUMB_PERCENT}%`,
            height: '100%',
            borderRadius: '99px',
            backgroundColor: interacting
              ? 'var(--color-primary-500)'
              : 'var(--color-primary-400)',
            cursor: draggingRef.current ? 'grabbing' : 'grab',
            touchAction: 'none',
            transition: 'background-color 150ms ease',
          }}
        />
      )}
    </div>
  );
};
